import { useNavigate } from 'react-router-dom';
import Hero from '../components/Hero';

const Membership = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Associate',
      price: '$59',
      priceInr: 'Rs. 4,999',
      period: 'year',
      type: 'Individual',
      features: [
        'ICCAC digital credential badge',
        'Evidence Literacy Orientation module',
        'ICCAC community access',
        'Resource library access',
        'Discounts on certification programs'
      ]
    },
    {
      name: 'Professional',
      price: '$119',
      priceInr: 'Rs. 9,999',
      period: 'year',
      type: 'Individual',
      popular: true,
      features: [
        'Everything in Associate',
        'Global Coach Directory listing',
        'Annual Evidence Literacy Update',
        'Research Bibliography access',
        'Client disclosure templates',
        'Peer supervision groups',
        'ICCAC Evidence & Practice Journal'
      ]
    },
    {
      name: 'Master',
      price: '$239',
      priceInr: 'Rs. 19,999',
      period: 'year',
      type: 'Individual',
      features: [
        'Everything in Professional',
        'Priority directory listing',
        'Speaking opportunities',
        'Standards Committee eligibility',
        'Conference keynote invitations',
        'Research grant access'
      ]
    },
    {
      name: 'Institution',
      price: 'Custom',
      priceInr: 'Contact us for pricing',
      period: '',
      type: 'Institutional',
      features: [
        'Multiple coach listings',
        'Co-branding & institutional recognition',
        'Annual Evidence Audit advisory support',
        'Bulk certification discounts',
        'L&D partnership options',
        'Custom onboarding'
      ]
    }
  ];

  const allBenefits = [
    {
      icon: '🎖️',
      title: 'Digital Credential Badge',
      description: 'QR-linked, independently verifiable. Display on your website, LinkedIn, and all professional materials with confidence.'
    },
    {
      icon: '📚',
      title: 'Annual Evidence Updates',
      description: 'Stay current with new research in your domain through our annual Evidence Literacy Update module, curated by domain experts.'
    },
    {
      icon: '🔍',
      title: 'Research Bibliography',
      description: 'A living, searchable database of peer-reviewed research across all 20 accredited coaching domains, updated quarterly.'
    },
    {
      icon: '📝',
      title: 'Disclosure Templates',
      description: 'Ready-to-use client disclosure forms and approved claim language guides for every evidence tier.'
    },
    {
      icon: '🌍',
      title: 'Global Community',
      description: 'Peer supervision groups, global summits, and masterclasses with consciousness-forward coaches worldwide.'
    },
    {
      icon: '📰',
      title: 'ICCAC Journal',
      description: 'Quarterly Evidence & Practice Journal: the latest research synthesis written for a practitioner audience, free for all members.'
    }
  ];

  return (
    <div>
      <Hero
        title="Join the ICCAC Global Community"
        subtitle="More than a credential, your connection to a global movement of coaches committed to elevating consciousness-based coaching"
      />

      {/* Pricing Plans */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`bg-white rounded-lg shadow-md overflow-hidden ${
                  plan.popular ? 'ring-4 ring-[#1e3a5f] relative' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-[#1e3a5f] text-white text-center py-2 text-sm font-semibold">
                    MOST POPULAR
                  </div>
                )}
                <div className="p-6">
                  <div className="text-sm text-gray-600 mb-2">{plan.type}</div>
                  <h3 className="text-2xl font-bold text-[#1e3a5f] mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    {plan.period && <span className="text-gray-600"> / {plan.period}</span>}
                    <div className="text-sm text-gray-600 mt-1">{plan.priceInr}</div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/contact')}
                    className={`w-full py-3 rounded-md font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-[#1e3a5f] text-white hover:bg-[#2d5380]'
                        : 'bg-gray-100 text-[#1e3a5f] hover:bg-gray-200'
                    }`}
                  >
                    {plan.name === 'Institution' ? 'Contact Us' : plan.name === 'Master' ? 'Apply' : 'Get Started'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* All Member Benefits */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4 text-center">All Member Benefits</h2>
          <p className="text-xl text-gray-600 text-center mb-12">What Every Member Gets</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allBenefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#1e3a5f] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Join the Global Community?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Connect with consciousness-forward coaches worldwide
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="bg-white text-[#1e3a5f] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors text-lg"
          >
            Get Started Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default Membership;